import React, { useId, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import { useTranslation } from 'react-i18next';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import ActionPathField from '../Actions/ActionPathField';
import TagEditor from '../Widgets/TagEditor';
import { appendFileNameToFolderPath } from '../../Utils/pathUtils';

const toBoolean = (value) => value === true || value === 1 || String(value).toLowerCase() === 'true';

const getOptionValue = (option) => (
  option && typeof option === 'object' ? option.value : option
);

const getOptionLabel = (option) => (
  option && typeof option === 'object' ? option.label ?? option.value : option
);

const TemplateSetupPathPicker = ({ field, value, helper, error, saving, caseid, tags, onChange, emphasized = false }) => {
  const { t } = useTranslation();
  const isFile = String(field?.type || '').toLowerCase() === 'file';
  const isOutputPath = String(field?.type || '').toLowerCase() === 'path'
    && field?.pathMode === 'output_file';
  const [browsing, setBrowsing] = useState(false);
  const {
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    loading: restrictionsLoading,
  } = useFolderRestrictions(caseid);
  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: isFile,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        onChange(isOutputPath
          ? appendFileNameToFolderPath(selectedPath, '{filename}.{file_extension}')
          : selectedPath);
      }
      setBrowsing(false);
    },
    onFolderBrowserClose: () => setBrowsing(false),
  });
  const selectedName = String(value || '').split(/[\\/]/).filter(Boolean).pop() || String(value || '');
  const chooseLabel = isFile
    ? t('template.setup_choose_file', 'Choose a file')
    : isOutputPath
      ? t('template.setup_choose_output_folder', 'Browse destination folders')
      : t('template.setup_choose_folder', 'Choose a folder');

  if (isOutputPath) {
    return (
      <Box sx={{ mt: 1.1 }}>
        <ActionPathField
          caseid={caseid}
          label={`${t(field.label || 'workflow.copy_file_target_label', 'Destination path')}${field.required ? ' *' : ''}`}
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || 'C:\\Output\\{filename}.{file_extension}'}
          disabled={saving}
          stacked
          tags={tags}
          browseLabel={restrictionsLoading
            ? t('template.setup_loading_file_locations', 'Loading available locations…')
            : chooseLabel}
          browseDisabled={restrictionsLoading || browsing}
          onBrowse={() => {
            setBrowsing(true);
            handleBrowseDirectory();
          }}
          error={error}
          helperText={error
            ? t('template.setup_required_value', 'Enter a value for this required field.')
            : helper || undefined}
        />
        <Tooltip title={t(
          'template.setup_output_path_help',
          'Choose a destination folder or enter the full path. The filename can include values from this project, such as {filename}.',
        )} arrow>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, cursor: 'help' }}>
            {t('template.setup_output_path_hint', 'You can insert project values in the path.')}
          </Typography>
        </Tooltip>
        {FolderBrowserComponent}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip
          title={isFile
            ? t('template.setup_choose_file_help', 'Choose a file from the folders available to this project.')
            : t('template.setup_choose_folder_help', 'Choose a folder available to this project.')}
          arrow
        >
          <span>
            <Button
              size={emphasized ? 'large' : 'small'}
              variant={emphasized && !selectedName ? 'contained' : 'outlined'}
              disabled={saving || restrictionsLoading || browsing}
              startIcon={restrictionsLoading
                ? <CircularProgress size={16} />
                : isFile ? <InsertDriveFileRoundedIcon /> : <FolderOpenRoundedIcon />}
              onClick={() => {
                setBrowsing(true);
                handleBrowseDirectory();
              }}
              sx={{ textTransform: 'none', fontWeight: 650, ...(emphasized ? { minHeight: 44, px: 2.5, borderRadius: 2 } : {}) }}
            >
              {restrictionsLoading
                ? t('template.setup_loading_file_locations', 'Loading available locations…')
                : chooseLabel}
            </Button>
          </span>
        </Tooltip>
        {(!emphasized || selectedName) && <Typography
          variant="body2"
          color={selectedName ? 'text.primary' : 'text.secondary'}
          aria-live="polite"
          sx={{ minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}
        >
          {selectedName
            ? t(isFile ? 'template.setup_selected_file' : 'template.setup_selected_folder', {
              defaultValue: isFile ? 'Selected: {{name}}' : 'Selected: {{name}}',
              name: selectedName,
            })
            : t(isFile ? 'template.setup_no_file_selected' : 'template.setup_no_folder_selected', {
              defaultValue: isFile ? 'No file selected yet' : 'No folder selected yet',
            })}
        </Typography>}
      </Box>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.65 }}>
          {helper}
        </Typography>
      )}
      {FolderBrowserComponent}
    </Box>
  );
};

const TemplateSetupField = ({
  field,
  value,
  caseid,
  helpText,
  example,
  saving = false,
  completed = false,
  error = '',
  existingConfigured = false,
  onChange,
  tags = [],
  enableTagEditor = false,
  emphasizeStoragePicker = false,
  scope,
}) => {
  const { t } = useTranslation();
  const editorId = useId();
  const type = String(field?.type || '').toLowerCase();
  const label = field?.label
    ? t(field.label, { defaultValue: field.label })
    : t('template.setup_answer', 'Your answer');
  const fieldHelper = field?.helperText
    ? t(field.helperText, { defaultValue: field.helperText })
    : '';
  const helper = [helpText, fieldHelper].filter(Boolean).join(' ');
  const normalizedValue = value ?? '';
  const isValueMissing = normalizedValue === null
    || normalizedValue === undefined
    || (typeof normalizedValue === 'string' && normalizedValue.trim() === '');
  const isNumberInvalid = type === 'number'
    && !isValueMissing
    && !Number.isFinite(Number(normalizedValue));
  const isRequiredMissing = field?.required === true
    && isValueMissing;
  const visibleError = error || (isNumberInvalid
    ? t('template.setup_invalid_number', 'Enter a valid number.')
    : '');

  let control;
  if (type === 'checkbox') {
    control = (
      <FormControlLabel
        control={(
          <Checkbox
            checked={toBoolean(normalizedValue)}
            disabled={saving}
            onChange={(event) => onChange(event.target.checked)}
          />
        )}
        label={label}
        sx={{ mt: 0.25, mb: 0.5, alignItems: 'flex-start' }}
      />
    );
  } else if (type === 'select') {
    const options = Array.isArray(field?.options) ? field.options : [];
    control = (
      <TextField
        select
        fullWidth
        size="small"
        required={field?.required === true}
        label={label}
        value={normalizedValue}
        disabled={saving}
        onChange={(event) => onChange(event.target.value)}
        helperText={helper || undefined}
      >
        {options.map((option, index) => {
          const optionValue = getOptionValue(option);
          const optionLabel = getOptionLabel(option);
          return (
            <MenuItem key={`${String(optionValue)}-${index}`} value={optionValue}>
              {t(optionLabel, { defaultValue: String(optionLabel ?? '') })}
            </MenuItem>
          );
        })}
      </TextField>
    );
  } else if (
    type === 'file'
    || type === 'folder'
    || (type === 'path' && field?.pathMode === 'output_file')
  ) {
    control = (
      <TemplateSetupPathPicker
        field={field}
        value={normalizedValue}
        helper={helper}
        error={isRequiredMissing}
        saving={saving}
        caseid={caseid}
        tags={tags}
        onChange={onChange}
        emphasized={emphasizeStoragePicker}
      />
    );
  } else if (enableTagEditor && field?.useTagEditor === true && ['text', 'textarea', 'tag'].includes(type)) {
    control = <Box>
      <Typography component="label" htmlFor={editorId} variant="body2" sx={{ display: 'block', mb: 0.75 }}>
        {label}{field.required ? ' *' : ''}
      </Typography>
      <TagEditor id={editorId} caseid={caseid} tags={tags} scope={scope} includeDashboardTags={false}
        aria-label={label} value={String(normalizedValue)} onChange={onChange} disabled={saving}
        placeholder={field.placeholder} singleLine={type !== 'textarea'} minRows={type === 'textarea' ? 3 : undefined} />
      {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
    </Box>;
  } else {
    const isTextArea = type === 'textarea';
    control = (
      <TextField
        fullWidth
        size="small"
        required={field?.required === true}
        label={label}
        type={type === 'password' ? 'password' : type === 'number' ? 'number' : 'text'}
        value={normalizedValue}
        disabled={saving}
        onChange={(event) => onChange(event.target.value)}
        placeholder={field?.placeholder || ''}
        helperText={helper || undefined}
        error={isNumberInvalid}
        multiline={isTextArea}
        minRows={isTextArea ? Math.min(Math.max(Number(field?.rows) || 3, 2), 6) : undefined}
        inputProps={type === 'number' ? { min: field?.min, max: field?.max, step: field?.step || 'any' } : undefined}
        autoComplete={type === 'password' ? 'new-password' : undefined}
      />
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      {control}
      {example && (
        <Box sx={{ mt: 1.5, px: 1.5, py: 1.25, border: '1px solid rgba(87, 87, 176, 0.12)', borderRadius: 2, backgroundColor: 'rgba(87, 87, 176, 0.045)' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35, fontWeight: 700 }}>
            {t('template.setup_example', 'Example')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {example}
          </Typography>
        </Box>
      )}
      {visibleError && (
        <Typography role="alert" variant="caption" color="error" sx={{ display: 'block', mt: 0.65 }}>
          {visibleError}
        </Typography>
      )}
      <Box sx={{ mt: 1.25 }}>
        {existingConfigured && !completed && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t(
              'template.setup_existing_value_help',
              'A value is already saved. Continue to keep it, or enter a replacement.',
            )}
          </Typography>
        )}
        {completed && !saving && (
          <Typography variant="caption" color="success.main" sx={{ fontWeight: 650 }}>
            {t('template.setup_saved', 'Saved to this project')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TemplateSetupField;
